import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Send, GraduationCap, IndianRupee } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { openWhatsApp, openPaymentPage, WHATSAPP_DISPLAY } from "@/lib/whatsapp";
import { payBookingAdvance } from "@/lib/razorpay";
import { getCurrentUser } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Caretaker / Attendant",
  "Nurse (GNM / B.Sc)",
  "Baby Caretaker",
  "Japa Maid",
  "Elder Care",
  "ICU at Home Setup",
];

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-navy placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-tealbrand/50 focus:border-tealbrand transition";

const Field = ({ label, children, testid }) => (
  <div className="space-y-1.5" data-testid={testid}>
    <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">{label}</label>
    {children}
  </div>
);

const buildMessage = (title, data) =>
  `${title}\n${Object.entries(data)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}: ${v}`)
    .join("\n")}\n— Sent via Sharpin Healthcare website`;

const Forms = ({ activeTab, onTabChange, presetService }) => {
  const [loading, setLoading] = useState("");

  const [request, setRequest] = useState({ name: "", phone: "", service: "", duration: "", city: "", notes: "" });
  const [requestSent, setRequestSent] = useState(false);
  const [requestLeadId, setRequestLeadId] = useState("");
  const [advancePaid, setAdvancePaid] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [accountEmail, setAccountEmail] = useState("");

  useEffect(() => {
    getCurrentUser().then((u) => {
      if (u?.email) setAccountEmail(u.email);
    });
  }, []);
  const [caretaker, setCaretaker] = useState({ name: "", phone: "", experience: "", shift: "", location: "", languages: "", education: "", patientTypes: "" });
  const [nurse, setNurse] = useState({ name: "", phone: "", qualification: "", registration: "", specialization: "", experience: "", shift: "", education: "", patientTypes: "" });
  const [vendor, setVendor] = useState({ company: "", contact: "", phone: "", email: "", category: "", gst: "" });

  useEffect(() => {
    if (presetService) setRequest((r) => ({ ...r, service: presetService }));
  }, [presetService]);

  const submit = async (type, data, waTitle, formKey) => {
    setLoading(formKey);
    const payload = accountEmail ? { ...data, "Account Email": accountEmail } : data;
    const message = buildMessage(waTitle, payload);
    try {
      const res = await axios.post(`${API}/leads`, { type, data: payload });
      if (formKey === "request") {
        toast.success("Request received! Our care team will contact you shortly with confirmation.");
        setRequestLeadId(res.data.id);
        setRequestSent(true);
      } else if (formKey === "vendor") {
        toast.success("Details saved! Opening WhatsApp to confirm with our care team.");
      } else {
        toast.success("Application received! Our team will contact you shortly.");
      }
    } catch {
      if (formKey === "request") {
        toast.error("Could not save your request. Please call us at +91-9211949414.");
      } else if (formKey === "vendor") {
        toast.error("Could not save details here — continuing on WhatsApp.");
      } else {
        toast.error("Could not save your application. Please call us at +91-9211949414.");
      }
    } finally {
      setLoading("");
      if (formKey === "vendor") openWhatsApp(message);
    }
  };

  const SubmitBtn = ({ formKey, label, testid }) => (
    <button
      type="submit"
      disabled={loading === formKey}
      data-testid={testid}
      className="group inline-flex items-center justify-center gap-2 bg-tealbrand text-white font-semibold px-8 py-4 rounded-full hover:bg-navy transition-colors duration-300 disabled:opacity-60 w-full sm:w-auto"
    >
      {loading === formKey ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
      {label}
    </button>
  );

  return (
    <section id="join" className="py-16 md:py-24" data-testid="forms-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <SectionHeading
          number="03 — Request & Join"
          title="One form away from"
          accent="the right care."
          subtitle="Every submission reaches our care coordinators instantly — whether you need care, or want to provide it."
          id="forms"
        />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-10"
        >
          <Tabs value={activeTab} onValueChange={onTabChange}>
            <TabsList className="flex flex-wrap h-auto gap-2 bg-alabaster p-2 rounded-2xl mb-10" data-testid="forms-tabs">
              <TabsTrigger value="request" data-testid="tab-request-care" className="rounded-xl px-5 py-2.5 text-sm font-semibold data-[state=active]:bg-navy data-[state=active]:text-white">
                Request Care
              </TabsTrigger>
              <TabsTrigger value="caretaker" data-testid="tab-become-caretaker" className="rounded-xl px-5 py-2.5 text-sm font-semibold data-[state=active]:bg-navy data-[state=active]:text-white">
                Become a Caretaker
              </TabsTrigger>
              <TabsTrigger value="nurse" data-testid="tab-become-nurse" className="rounded-xl px-5 py-2.5 text-sm font-semibold data-[state=active]:bg-navy data-[state=active]:text-white">
                Become a Nurse
              </TabsTrigger>
              <TabsTrigger value="vendor" data-testid="tab-vendor" className="rounded-xl px-5 py-2.5 text-sm font-semibold data-[state=active]:bg-navy data-[state=active]:text-white">
                Vendor Registration
              </TabsTrigger>
            </TabsList>

            <TabsContent value="request" data-testid="form-request-care">
              <form
                className="grid sm:grid-cols-2 gap-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  submit(
                    "service_request",
                    { "Full Name": request.name, Phone: request.phone, "Service Required": request.service, Duration: request.duration, "Location / City": request.city, "Patient Details / Requirements": request.notes },
                    "New Care Request — Sharpin Healthcare",
                    "request"
                  );
                }}
              >
                <Field label="Full Name"><input required data-testid="request-name-input" className={inputCls} placeholder="Your name" value={request.name} onChange={(e) => setRequest({ ...request, name: e.target.value })} /></Field>
                <Field label="Phone Number"><input required type="tel" data-testid="request-phone-input" className={inputCls} placeholder="98765 43210" value={request.phone} onChange={(e) => setRequest({ ...request, phone: e.target.value })} /></Field>
                <Field label="Service Required">
                  <Select value={request.service} onValueChange={(v) => setRequest({ ...request, service: v })} required>
                    <SelectTrigger data-testid="request-service-select" className={inputCls}><SelectValue placeholder="Select a service" /></SelectTrigger>
                    <SelectContent>{SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Duration">
                  <Select value={request.duration} onValueChange={(v) => setRequest({ ...request, duration: v })} required>
                    <SelectTrigger data-testid="request-duration-select" className={inputCls}><SelectValue placeholder="Care duration" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12 Hours (Day / Night shift)">12 Hours (Day / Night shift)</SelectItem>
                      <SelectItem value="24 Hours Live-In">24 Hours Live-In</SelectItem>
                      <SelectItem value="Short Visit / Procedure">Short Visit / Procedure</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Location / City"><input required data-testid="request-city-input" className={inputCls} placeholder="e.g. Rohini, Delhi" value={request.city} onChange={(e) => setRequest({ ...request, city: e.target.value })} /></Field>
                <Field label="Patient Vitals / Special Requirements"><textarea data-testid="request-notes-textarea" rows={1} className={inputCls} placeholder="Age, condition, mobility, equipment needed…" value={request.notes} onChange={(e) => setRequest({ ...request, notes: e.target.value })} /></Field>
                <div className="sm:col-span-2 pt-2"><SubmitBtn formKey="request" label="Submit Care Request" testid="request-submit-btn" /></div>
              </form>
              {requestSent && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="mt-6 bg-tealbrand-light/60 border border-tealbrand/30 rounded-2xl p-6"
                  data-testid="request-success-panel"
                >
                  {advancePaid ? (
                    <>
                      <p className="font-display font-semibold text-navy mb-1">Advance paid — slot secured!</p>
                      <p className="text-sm text-slate-600 leading-relaxed" data-testid="advance-paid-message">
                        Your ₹500 payment is verified. Our care team is confirming your booking on
                        WhatsApp right now.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-display font-semibold text-navy mb-1">Request received!</p>
                      <p className="text-sm text-slate-600 mb-5 leading-relaxed">
                        Our care team will call or email you shortly with confirmation. Want priority
                        deployment of your caretaker or nurse? Secure your slot with a fully adjustable
                        ₹500 booking advance.
                      </p>
                      <label className="flex items-start gap-2.5 mb-5 cursor-pointer max-w-lg" data-testid="terms-accept-label">
                        <input
                          type="checkbox"
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          data-testid="terms-accept-checkbox"
                          className="mt-1 h-4 w-4 accent-tealbrand shrink-0"
                        />
                        <span className="text-sm text-slate-600 leading-relaxed">
                          I have read and accept the{" "}
                          <Link to="/terms" className="text-tealbrand-dark font-semibold underline" data-testid="terms-accept-link">
                            Terms of Service
                          </Link>
                          , including the advance payment and zero-cancellation policy.
                        </span>
                      </label>
                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          disabled={!termsAccepted}
                          onClick={() =>
                            payBookingAdvance({
                              name: request.name,
                              phone: request.phone,
                              leadId: requestLeadId,
                              termsAccepted,
                              onSuccess: () => {
                                setAdvancePaid(true);
                                toast.success("₹500 advance received — payment verified!");
                              },
                              onFailure: () =>
                                toast.error("Payment not completed. You can pay anytime via Pay Now."),
                            })
                          }
                          data-testid="request-pay-advance-btn"
                          className="inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-tealbrand transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <IndianRupee className="h-4 w-4" /> Pay ₹500 Booking Advance
                        </button>
                        <button
                          onClick={() => setRequestSent(false)}
                          data-testid="request-pay-later-btn"
                          className="text-sm font-semibold text-slate-500 hover:text-navy transition-colors"
                        >
                          Maybe later
                        </button>
                      </div>
                    </>
                  )}
                </motion.div>
              )}
            </TabsContent>

            <TabsContent value="caretaker" data-testid="form-become-caretaker">
              <form
                className="grid sm:grid-cols-2 gap-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  submit(
                    "caretaker_application",
                    { "Full Name": caretaker.name, Phone: caretaker.phone, "Years of Experience": caretaker.experience, "Preferred Shift Timing": caretaker.shift, "Current Location": caretaker.location, "Languages Spoken": caretaker.languages, "Education / Qualification": caretaker.education, "Patient Types They Can Handle": caretaker.patientTypes },
                    "Caretaker Application — Sharpin Healthcare",
                    "caretaker"
                  );
                }}
              >
                <Field label="Full Name"><input required data-testid="caretaker-name-input" className={inputCls} placeholder="Your name" value={caretaker.name} onChange={(e) => setCaretaker({ ...caretaker, name: e.target.value })} /></Field>
                <Field label="Phone Number"><input required type="tel" data-testid="caretaker-phone-input" className={inputCls} placeholder="98765 43210" value={caretaker.phone} onChange={(e) => setCaretaker({ ...caretaker, phone: e.target.value })} /></Field>
                <Field label="Years of Experience"><input required data-testid="caretaker-experience-input" className={inputCls} placeholder="e.g. 4 years" value={caretaker.experience} onChange={(e) => setCaretaker({ ...caretaker, experience: e.target.value })} /></Field>
                <Field label="Preferred Shift Timing">
                  <Select value={caretaker.shift} onValueChange={(v) => setCaretaker({ ...caretaker, shift: v })} required>
                    <SelectTrigger data-testid="caretaker-shift-select" className={inputCls}><SelectValue placeholder="Select shift timing" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Day Shift (8 AM – 8 PM)">Day Shift (8 AM – 8 PM)</SelectItem>
                      <SelectItem value="Night Shift (8 PM – 8 AM)">Night Shift (8 PM – 8 AM)</SelectItem>
                      <SelectItem value="24 Hours Live-In">24 Hours Live-In</SelectItem>
                      <SelectItem value="Flexible / Any">Flexible / Any</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Current Location"><input required data-testid="caretaker-location-input" className={inputCls} placeholder="Area, City" value={caretaker.location} onChange={(e) => setCaretaker({ ...caretaker, location: e.target.value })} /></Field>
                <Field label="Languages Spoken"><input data-testid="caretaker-languages-input" className={inputCls} placeholder="Hindi, English…" value={caretaker.languages} onChange={(e) => setCaretaker({ ...caretaker, languages: e.target.value })} /></Field>
                <Field label="Education / Qualification Details"><input required data-testid="caretaker-education-input" className={inputCls} placeholder="e.g. 12th pass, GDA training certificate" value={caretaker.education} onChange={(e) => setCaretaker({ ...caretaker, education: e.target.value })} /></Field>
                <Field label="Patient Types You Can Handle">
                  <Select value={caretaker.patientTypes} onValueChange={(v) => setCaretaker({ ...caretaker, patientTypes: v })} required>
                    <SelectTrigger data-testid="caretaker-patient-types-select" className={inputCls}><SelectValue placeholder="Select patient type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Elderly Care">Elderly Care</SelectItem>
                      <SelectItem value="Bedridden Patients">Bedridden Patients</SelectItem>
                      <SelectItem value="Post-Surgery Recovery">Post-Surgery Recovery</SelectItem>
                      <SelectItem value="Dementia / Alzheimer's">Dementia / Alzheimer's</SelectItem>
                      <SelectItem value="Baby / Child Care">Baby / Child Care</SelectItem>
                      <SelectItem value="Any / All">Any / All</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <div className="sm:col-span-2 pt-2"><SubmitBtn formKey="caretaker" label="Submit Application" testid="caretaker-submit-btn" /></div>
              </form>
            </TabsContent>

            <TabsContent value="nurse" data-testid="form-become-nurse">
              <div className="mb-8 flex items-start gap-3 bg-tealbrand-light/60 border border-tealbrand/20 rounded-2xl px-5 py-4" data-testid="nurse-eligibility-note">
                <GraduationCap className="h-5 w-5 text-tealbrand-dark shrink-0 mt-0.5" />
                <p className="text-sm font-medium text-tealbrand-dark">
                  Only <strong>GNM</strong> (General Nursing &amp; Midwifery) and <strong>B.Sc</strong> graduates can apply. Nursing Council registration is verified before deployment.
                </p>
              </div>
              <form
                className="grid sm:grid-cols-2 gap-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  submit(
                    "nurse_application",
                    { "Full Name": nurse.name, Phone: nurse.phone, Qualification: nurse.qualification, "Nursing Council Reg. No.": nurse.registration, Specialization: nurse.specialization, "Years of Clinical Experience": nurse.experience, "Preferred Shift Timing": nurse.shift, "Education Details": nurse.education, "Patient Types They Can Handle": nurse.patientTypes },
                    "Nurse Application (GNM / B.Sc) — Sharpin Healthcare",
                    "nurse"
                  );
                }}
              >
                <Field label="Full Name"><input required data-testid="nurse-name-input" className={inputCls} placeholder="Your name" value={nurse.name} onChange={(e) => setNurse({ ...nurse, name: e.target.value })} /></Field>
                <Field label="Phone Number"><input required type="tel" data-testid="nurse-phone-input" className={inputCls} placeholder="98765 43210" value={nurse.phone} onChange={(e) => setNurse({ ...nurse, phone: e.target.value })} /></Field>
                <Field label="Qualification (GNM / B.Sc only)">
                  <Select value={nurse.qualification} onValueChange={(v) => setNurse({ ...nurse, qualification: v })} required>
                    <SelectTrigger data-testid="nurse-qualification-select" className={inputCls}><SelectValue placeholder="Scroll & select your qualification" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GNM — General Nursing & Midwifery">GNM — General Nursing &amp; Midwifery</SelectItem>
                      <SelectItem value="B.Sc Nursing">B.Sc Nursing</SelectItem>
                      <SelectItem value="B.Sc Medical">B.Sc Medical</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Nursing Council Registration No."><input required data-testid="nurse-registration-input" className={inputCls} placeholder="e.g. DNC/12345" value={nurse.registration} onChange={(e) => setNurse({ ...nurse, registration: e.target.value })} /></Field>
                <Field label="Specialization">
                  <Select value={nurse.specialization} onValueChange={(v) => setNurse({ ...nurse, specialization: v })} required>
                    <SelectTrigger data-testid="nurse-specialization-select" className={inputCls}><SelectValue placeholder="Select specialization" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ICU / Critical Care">ICU / Critical Care</SelectItem>
                      <SelectItem value="Post-Operative Care">Post-Operative Care</SelectItem>
                      <SelectItem value="General / Home Nursing">General / Home Nursing</SelectItem>
                      <SelectItem value="Mother & Baby Care">Mother &amp; Baby Care</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Years of Clinical Experience"><input required data-testid="nurse-experience-input" className={inputCls} placeholder="e.g. 3 years" value={nurse.experience} onChange={(e) => setNurse({ ...nurse, experience: e.target.value })} /></Field>
                <Field label="Preferred Shift Timing">
                  <Select value={nurse.shift} onValueChange={(v) => setNurse({ ...nurse, shift: v })} required>
                    <SelectTrigger data-testid="nurse-shift-select" className={inputCls}><SelectValue placeholder="Select shift timing" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Day Shift (8 AM – 8 PM)">Day Shift (8 AM – 8 PM)</SelectItem>
                      <SelectItem value="Night Shift (8 PM – 8 AM)">Night Shift (8 PM – 8 AM)</SelectItem>
                      <SelectItem value="24 Hours Live-In">24 Hours Live-In</SelectItem>
                      <SelectItem value="Flexible / Any">Flexible / Any</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Education Details"><input required data-testid="nurse-education-input" className={inputCls} placeholder="College / University, year of passing" value={nurse.education} onChange={(e) => setNurse({ ...nurse, education: e.target.value })} /></Field>
                <Field label="Patient Types You Can Handle">
                  <Select value={nurse.patientTypes} onValueChange={(v) => setNurse({ ...nurse, patientTypes: v })} required>
                    <SelectTrigger data-testid="nurse-patient-types-select" className={inputCls}><SelectValue placeholder="Select patient type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ICU / Critical Care Patients">ICU / Critical Care Patients</SelectItem>
                      <SelectItem value="Post-Operative Patients">Post-Operative Patients</SelectItem>
                      <SelectItem value="Elderly Patients">Elderly Patients</SelectItem>
                      <SelectItem value="Bedridden Patients">Bedridden Patients</SelectItem>
                      <SelectItem value="Mother & Newborn">Mother &amp; Newborn</SelectItem>
                      <SelectItem value="Any / All">Any / All</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <div className="sm:col-span-2 pt-2"><SubmitBtn formKey="nurse" label="Submit Application" testid="nurse-submit-btn" /></div>
              </form>
            </TabsContent>

            <TabsContent value="vendor" data-testid="form-vendor">
              <form
                className="grid sm:grid-cols-2 gap-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  submit(
                    "vendor_registration",
                    { "Company / Agency": vendor.company, "Contact Person": vendor.contact, Phone: vendor.phone, Email: vendor.email, Category: vendor.category, "GST / Registration No.": vendor.gst },
                    "Vendor Partnership Inquiry — Sharpin Healthcare",
                    "vendor"
                  );
                }}
              >
                <Field label="Company / Agency Name"><input required data-testid="vendor-company-input" className={inputCls} placeholder="Company name" value={vendor.company} onChange={(e) => setVendor({ ...vendor, company: e.target.value })} /></Field>
                <Field label="Contact Person"><input required data-testid="vendor-contact-input" className={inputCls} placeholder="Your name" value={vendor.contact} onChange={(e) => setVendor({ ...vendor, contact: e.target.value })} /></Field>
                <Field label="Phone Number"><input required type="tel" data-testid="vendor-phone-input" className={inputCls} placeholder="98765 43210" value={vendor.phone} onChange={(e) => setVendor({ ...vendor, phone: e.target.value })} /></Field>
                <Field label="Email"><input required type="email" data-testid="vendor-email-input" className={inputCls} placeholder="you@company.com" value={vendor.email} onChange={(e) => setVendor({ ...vendor, email: e.target.value })} /></Field>
                <Field label="Category">
                  <Select value={vendor.category} onValueChange={(v) => setVendor({ ...vendor, category: v })} required>
                    <SelectTrigger data-testid="vendor-category-select" className={inputCls}><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Medical Equipment Rental">Medical Equipment Rental</SelectItem>
                      <SelectItem value="Oxygen Cylinder Supply">Oxygen Cylinder Supply</SelectItem>
                      <SelectItem value="Manpower / Staffing Partner">Manpower / Staffing Partner</SelectItem>
                      <SelectItem value="Lab & Diagnostics Services">Lab &amp; Diagnostics Services</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="GST / Registration No."><input data-testid="vendor-gst-input" className={inputCls} placeholder="Optional" value={vendor.gst} onChange={(e) => setVendor({ ...vendor, gst: e.target.value })} /></Field>
                <div className="sm:col-span-2 pt-2"><SubmitBtn formKey="vendor" label="Register via WhatsApp" testid="vendor-submit-btn" /></div>
              </form>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </section>
  );
};

export default Forms;
